"use client"

import type React from "react"
import { useState, useEffect, useMemo } from "react"
import { AddCertificationOffcanvas } from "./add"
import { EditCertificationOffcanvas } from "./edit"
import { CertificationsList, type CertificationFilters } from "./list"
import api from "../../../libs/axios"
import toast from "react-hot-toast"
import { ConfirmModal } from "../../../components/ConfirmModal"
import { TrainingCertification, TrainingType, TrainingInstitute, Pagination, TrainingRequest } from "../types"
import type { SelectOption } from "../../../utils/select-with-search"

export const TrainingCertificationsManagement: React.FC = () => {
  const [certifications, setCertifications] = useState<TrainingCertification[]>([])
  const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([])
  const [institutes, setInstitutes] = useState<TrainingInstitute[]>([])
  const [confirmVerifyId, setConfirmVerifyId] = useState<number | null>(null)
  const [trainingRequests, setTrainingRequests] = useState<TrainingRequest[]>([])
  const [pagination, setPagination] = useState<Pagination>({
    offset: 0, limit: 10, page: 1, total_pages: 1, total_count: 0,
    has_next_page: false, has_prev_page: false, next_page: null, prev_page: null,
  })

  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [selectedCert, setSelectedCert] = useState<TrainingCertification | null>(null)
  const [deductionFilter, setDeductionFilter] = useState<string>("")
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: "ASC" | "DESC" }>({
    field: "created_at",
    direction: "DESC",
  })
  const [advancedFilters, setAdvancedFilters] = useState<CertificationFilters>({})

  const fetchCertifications = async (
    deduction = "",
    page = 1,
    limitValue?: number,
    sort = sortConfig,
    filters = advancedFilters,
  ) => {
    setLoading(true)
    try {
      const effectiveLimit = limitValue ?? pagination.limit ?? 10
      const queryParams: any = { page, limit: effectiveLimit }
      if (deduction) queryParams.deduction_status = deduction
      if (sort?.field) {
        queryParams.sort_by = sort.field
        queryParams.sort_order = sort.direction
      }
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (key.endsWith("_label")) return
        if (value !== undefined && value !== "") {
          queryParams[key] = value
        }
      })

      const response = await api.get("/training/certifications/list", { params: queryParams })
      if (response.data.success) {
        const certificationData =
          response.data.data?.certifications ??
          response.data.data?.training_certifications ??
          []
        setCertifications(certificationData)
        setPagination(response.data.data.pagination || pagination)
      }
    } catch (error) {
      console.error("Error fetching certifications:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchDropdowns = async () => {
    try {
      const [typesRes, institutesRes, requestsRes] = await Promise.all([
        api.get("/training/courses/active"),
        api.get("/training/institutes/active"),
        api.get("/training/requests/list", { params: { page: 1, limit: 100, status: "paid" } }),
      ])
      if (typesRes.data.success) {
        const typesData =
          typesRes.data.data?.training_types ??
          typesRes.data.data?.types ??
          []
        setTrainingTypes(typesData)
      }
      if (institutesRes.data.success) setInstitutes(institutesRes.data.data.institutes || [])
      if (requestsRes.data.success) {
        const requestData =
          requestsRes.data.data?.training_requests ??
          requestsRes.data.data?.requests ??
          []
        setTrainingRequests(requestData)
      }
    } catch (error) {
      console.error("Error fetching dropdowns:", error)
    }
  }

  const createCertification = async (data: any) => {
    setActionLoading(true)
    try {
      const response = await api.post("/training/certifications/create", data)
      if (response.data.success) {
        toast.success("Certification created successfully")
        await fetchCertifications(deductionFilter, 1, undefined, sortConfig, advancedFilters)
        setShowAdd(false)
      }
    } catch (error: any) {
      console.error("Error creating certification:", error)
      toast.error(error?.response?.data?.message || "Failed to create certification")
    } finally {
      setActionLoading(false)
    }
  }

  const updateCertification = async (data: any) => {
    setActionLoading(true)
    try {
      const response = await api.put(`/training/certifications/${data.id}`, data)
      if (response.data.success) {
        await fetchCertifications(deductionFilter, pagination.page, undefined, sortConfig, advancedFilters)
        setShowEdit(false)
        setSelectedCert(null)
      }
    } catch (error) {
      console.error("Error updating certification:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const updateDeduction = async (id: number, data: any) => {
    setActionLoading(true)
    try {
      const response = await api.patch(`/training/certifications/${id}/deduction`, data)
      if (response.data.success) {
        await fetchCertifications(deductionFilter, pagination.page, undefined, sortConfig, advancedFilters)
      }
    } catch (error) {
      console.error("Error updating deduction:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const verifyCertification = (id: number) => {
    setConfirmVerifyId(id)
  }

  const executeVerifyCertification = async () => {
    if (confirmVerifyId === null) return
    setActionLoading(true)
    try {
      const response = await api.patch(`/training/certifications/${confirmVerifyId}/verify`)
      if (response.data.success) {
        await fetchCertifications(deductionFilter, pagination.page, undefined, sortConfig, advancedFilters)
      }
    } catch (error) {
      console.error("Error verifying certification:", error)
    } finally {
      setActionLoading(false)
      setConfirmVerifyId(null)
    }
  }

  const handleDeductionFilter = (status: string) => { setDeductionFilter(status); fetchCertifications(status, 1, undefined, sortConfig, advancedFilters) }
  const handlePageChange = (page: number) => { fetchCertifications(deductionFilter, page, undefined, sortConfig, advancedFilters) }
  const handleLimitChange = (limit: number) => { setPagination(prev => ({ ...prev, limit, page: 1 })); fetchCertifications(deductionFilter, 1, limit, sortConfig, advancedFilters) }

  const handleSort = (field: string, direction: "ASC" | "DESC") => {
    const sort = { field, direction }
    setSortConfig(sort)
    fetchCertifications(deductionFilter, pagination.page, undefined, sort, advancedFilters)
  }

  const handleAdvancedFilter = (filters: CertificationFilters) => {
    setAdvancedFilters(filters)
    fetchCertifications(deductionFilter, 1, undefined, sortConfig, filters)
  }

  const handleEdit = (cert: TrainingCertification) => { setSelectedCert(cert); setShowEdit(true) }
  const handleCloseModals = () => { setShowAdd(false); setShowEdit(false); setSelectedCert(null) }

  useEffect(() => { fetchCertifications(); fetchDropdowns() }, [])

  const trainingTypeOptions = useMemo<SelectOption[]>(() => (
    trainingTypes.map((type) => ({ id: type.id, value: type.id, label: type.training_name }))
  ), [trainingTypes])

  const trainingInstituteOptions = useMemo<SelectOption[]>(() => (
    institutes.map((inst) => ({ id: inst.id, value: inst.id, label: inst.institute_name }))
  ), [institutes])

  const trainingRequestOptions = useMemo<SelectOption[]>(() => (
    trainingRequests.map((request) => ({ id: request.id, value: request.id, label: request.request_id }))
  ), [trainingRequests])

  return (
    <div>
      <CertificationsList
        certifications={certifications} pagination={pagination} onDeductionFilter={handleDeductionFilter}
        onPageChange={handlePageChange} onLimitChange={handleLimitChange} onAdd={() => setShowAdd(true)}
        onEdit={handleEdit} onVerify={verifyCertification} loading={loading} sortConfig={sortConfig} onSort={handleSort}
        onAdvancedFilter={handleAdvancedFilter} advancedFilters={advancedFilters}
        trainingTypeOptions={trainingTypeOptions} trainingInstituteOptions={trainingInstituteOptions}
        trainingRequestOptions={trainingRequestOptions}
      />
      <AddCertificationOffcanvas
        isOpen={showAdd}
        onClose={handleCloseModals}
        onSubmit={createCertification}
        trainingTypes={trainingTypes}
        institutes={institutes}
        trainingRequestOptions={trainingRequestOptions}
        loading={actionLoading}
      />
      <EditCertificationOffcanvas isOpen={showEdit} onClose={handleCloseModals} onSubmit={updateCertification} certification={selectedCert} trainingTypes={trainingTypes} institutes={institutes} loading={actionLoading} />

      <ConfirmModal
        isOpen={confirmVerifyId !== null}
        onClose={() => setConfirmVerifyId(null)}
        onConfirm={executeVerifyCertification}
        title="Verify Certification"
        message="Are you sure you want to verify this certification? This action cannot be undone."
        confirmText="Verify"
        variant="warning"
        loading={actionLoading}
      />
    </div>
  )
}
